import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import { GADataProps } from "@components/GAData/GAData";
import EventsAwardsHeader from "@events-awards/components/EventAwardsHeader";
import EventsAwardsFooter from "@events-awards/components/EventsAwardsFooter";
import EventsAwardsNavigationBar from "@events-awards/components/EventsAwardsNavigationBarComponents/EventAwardsNavigationBar";
import { EventsAwardsNewsPageData } from "@events-awards/EventsAwardsNews/utils/types";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { CardVariations } from "@particles/card/utils/types";
import Listing from "@particles/listing/Listing";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { cn } from "@util/helpers";

/**
 * Component to render news page content
 */
const EventsAwardsNewsPageContent = ({
  articles,
  awardType,
  entity,
  gaProps,
}: EventsAwardsNewsPageData & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: "font-public-sans",
    },
    image: {
      width: "w-2/5 lg:w-1/6 h-fit",
      position: "left",
      extraClass: "lg:order-2",
      filters: { w: ImagePresets.MICRO, dpr: 1, f: "webp" },
      aspectRatio: "aspect-3x2",
      imageClassName: cn("object-cover"),
    },
    content: {
      width: "w-3/5 lg:w-5/6",
      extraClass: cn("pl-4 lg:order-1 lg:pl-0 lg:pr-4"),
      blurb: {
        extraClass: cn("text-gray-750 font-sans"),
      },
      updatedTimeExtraClass: "text-7xs font-public-sans",
      updatedTimeWrapperClass: "lg:mt-auto",
    },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: "font-public-sans my-1",
    },
  };

  return (
    <div
      className="min-h-screen w-full bg-gray-50"
      data-testid="events-awards-news-page-content-component"
    >
      <EventsAwardsHeader />

      <Container rootClassName="!mb-8">
        <EventsAwardsNavigationBar awardType={awardType} />

        <BlockTitle
          text="News"
          rootClassName="font-poppins text-4xl font-medium text-gray-850 md:text-6xl lg:text-8xl mb-2"
        />

        <Listing
          cardProps={articles}
          sections={entity.sections[0].uniqueName}
          templateGCardVariantOverride={variant}
          size={10}
          pageType="section"
          offset={0}
        />
      </Container>

      <EventsAwardsFooter awardType={awardType} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsNewsPageContent;
